import React, { useState } from "react"

import { useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import { Color, Size } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import { StyledLink, WithLoadingIndicator } from "~tailwindui/Basics"
import Table from "~tailwindui/Table/Table"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import classNames from "src/classNames"

import LocalTime from "components/LocalTime"
import TimeProtectedLink from "components/TimeProtectedLink"
import TimeProtectedMiroLink from "components/TimeProtectedMiroLink"

import TutorCloudroomButton from "./TutorCloudroomButton"

export type ScheduledSessionsProps = {
  hideIfEmpty?: boolean
}

const ScheduledSessions: React.FC<ScheduledSessionsProps> = ({
  hideIfEmpty = false,
}) => {
  const history = useHistory()
  const [page, setPage] = useState(1)
  const [result] = useQuery({
    query: scheduledSessionsQuery,
    variables: { page },
  })

  if (
    hideIfEmpty &&
    result.data &&
    result.data.viewer.sessions.data.length === 0
  )
    return null

  return (
    <WithLoadingIndicator
      result={result}
      error="An error occurred while loading your scheduled sessions"
    >
      {({ data: { viewer } }) => (
        <Table
          title="Scheduled Sessions"
          changePage={setPage}
          data={viewer.sessions}
          rowCallback={({ id }) => history.push(path("sessionDetails", { id }))}
          cols={[
            {
              header: "Study Group",
              value: session =>
                session.studyGroup ? (
                  <StyledLink
                    key={session.studyGroup.id}
                    to={path("studyGroupDetails", {
                      id: session.studyGroup.id,
                    })}
                  >
                    <span className="flex items-center">
                      {session.studyGroup.name}
                    </span>
                  </StyledLink>
                ) : null,
            },
            { header: "Subject", value: session => session.subject.name },
            {
              header: "Students",
              value: session => (
                <ul key={session.id}>
                  {session.students.map(student => (
                    <li key={student.id}>
                      <StyledLink
                        to={path("studentDetails", {
                          id: student.id,
                        })}
                      >
                        {student.fullName}
                      </StyledLink>
                    </li>
                  ))}
                </ul>
              ),
            },
            {
              header: "Date",
              value: session => (
                <div key={session.id} className="min-w-[80px]">
                  <LocalTime format="ddd MMM D" timestamp={session.startsAt} />
                  <br />
                  <LocalTime omitDate timestamp={session.startsAt} />
                  &mdash;
                  <LocalTime omitDate timestamp={session.endsAt} />
                </div>
              ),
            },
            {
              header: "Actions",
              value: session => (
                <div
                  className="flex flex-wrap items-center gap-3 sm:gap-1"
                  onClick={e => {
                    e.stopPropagation()
                  }}
                >
                  {session.cloudroomActive && (
                    <TutorCloudroomButton
                      startsAt={session.startsAt}
                      id={session.id}
                    />
                  )}
                  {session.realtimeboards.length > 0 && (
                    <TimeProtectedMiroLink
                      tailwind
                      startsAt={session.startsAt}
                      email={viewer.ssoEmail}
                      boardId={session.realtimeboards[0].id}
                      boardPending={session.realtimeboardPending}
                      buttonClassName={buttonClassNames({
                        size: Size.Small,
                        color: Color.Green,
                      })}
                    />
                  )}
                  {session.googledocUrl && (
                    <TimeProtectedLink
                      tailwind
                      text="Google Doc"
                      href={session.googledocUrl}
                      startsAt={session.startsAt}
                      className={classNames(
                        buttonClassNames({
                          color: Color.Blue,
                          size: Size.Small,
                        })
                      )}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
    </WithLoadingIndicator>
  )
}

const scheduledSessionsQuery = gql`
  query ScheduledSessions($page: Int!) {
    viewer {
      sessions(
        filters: { future: true, statusState: scheduled, billState: unbilled }
        page: $page
      ) {
        currentPage
        totalPages
        data {
          id
          startsAt
          endsAt
          future
          immovable
          mayCancelByTutor
          cloudroomActive
          googledocUrl
          studyGroup {
            id
            name
          }
          students {
            id
            fullName
          }
          subject {
            name
          }
          realtimeboardPending
          realtimeboards {
            id
          }
        }
      }
    }
  }
`

export default ScheduledSessions
